if (!customElements.get('order-agreement')) {
  customElements.define(
    'order-agreement',
    class OrderAgreement extends HTMLElement {
      connectedCallback() {
        if (this.initialised) return;
        this.initialised = true;

        this.form = this.closest('form');
        this.productForm = this.closest('product-form');
        this.productInfo = this.closest('product-info');
        this.checkbox = this.querySelector('[data-order-acknowledgement]');
        this.variantInput = this.form?.querySelector('[name="id"]');
        this.quantityInput = this.productInfo?.querySelector('.quantity__input');
        this.type = this.dataset.agreementType;

        const variantData = this.querySelector('[data-order-variant-data]');
        try {
          this.variants = variantData ? JSON.parse(variantData.textContent) : {};
        } catch (error) {
          console.error('Unable to read order agreement variant data.', error);
          this.variants = {};
        }

        this.refreshHandler = this.refresh.bind(this);
        this.checkbox?.addEventListener('change', this.refreshHandler);
        this.variantInput?.addEventListener('change', this.refreshHandler);
        this.quantityInput?.addEventListener('input', this.refreshHandler);
        this.quantityInput?.addEventListener('change', this.refreshHandler);
        window.addEventListener('order-agreement:refresh', this.refreshHandler);

        this.refresh();
      }

      disconnectedCallback() {
        this.checkbox?.removeEventListener('change', this.refreshHandler);
        this.variantInput?.removeEventListener('change', this.refreshHandler);
        this.quantityInput?.removeEventListener('input', this.refreshHandler);
        this.quantityInput?.removeEventListener('change', this.refreshHandler);
        window.removeEventListener('order-agreement:refresh', this.refreshHandler);
      }

      madeToOrderIsRequired() {
  const variantId = String(this.variantInput?.value || '');
  const variant = this.variants?.[variantId];

  if (!variant) return false;

  const inventory = Number.parseInt(variant.inventory, 10);
  const cartQuantity = Number.parseInt(variant.cartQuantity, 10) || 0;

  const quantityInput =
    this.productInfo?.querySelector('.quantity__input') ||
    this.form?.querySelector('[name="quantity"]');

  const quantityToAdd = Math.max(
    1,
    Number.parseInt(quantityInput?.value, 10) || 1
  );

  return (
    variant.management === 'shopify' &&
    variant.policy === 'continue' &&
    (
      inventory <= 0 ||
      cartQuantity + quantityToAdd > inventory
    )
  );
}



      refresh() {
        if (!this.checkbox) return;

        const active = this.type === 'made-to-order' ? this.madeToOrderIsRequired() : true;
        this.classList.toggle('hidden', !active);
        this.checkbox.disabled = !active;
        this.checkbox.required = active;

        if (!active) {
          this.checkbox.checked = false;
          this.classList.remove('order-warning--needs-acceptance');
        } else if (this.checkbox.checked) {
          this.classList.remove('order-warning--needs-acceptance');
        }

        this.productForm?.refreshOrderAgreement();
      }
    }
  );
}

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        if (!this.form) return;
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        if (!this.submitButton) return;
        this.submitButtonText = this.submitButton.querySelector('span');
        this.productUnavailable = this.submitButton.dataset.productUnavailable === 'true';

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        queueMicrotask(() => this.refreshOrderAgreement());
      }

      onSubmitHandler(evt) {
        evt.preventDefault();

        if (!this.validateOrderAcknowledgements()) return;
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);

        // Guarantee that accepted made-to-order and special-order terms are
        // saved to the order as line-item properties, even when the checkbox
        // sits outside the form and is linked using the HTML form attribute.
        this.getActiveOrderAcknowledgements().forEach((checkbox) => {
          if (checkbox.checked && checkbox.name && !formData.has(checkbox.name)) {
            formData.append(checkbox.name, checkbox.value || 'Yes');
          }
        });

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure('add:paint-updated-sections', () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure('add:paint-updated-sections', () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent('add:user-action', evt);
          });
      }

      getActiveOrderAcknowledgements() {
        const scope = this.closest('product-info') || this;
        return Array.from(scope.querySelectorAll('[data-order-acknowledgement]')).filter((checkbox) => {
          const warning = checkbox.closest('[data-order-warning]');
          return warning && !warning.classList.contains('hidden') && !checkbox.disabled;
        });
      }

      hasPendingOrderAgreement() {
        return this.getActiveOrderAcknowledgements().some((checkbox) => !checkbox.checked);
      }

      refreshOrderAgreement() {
        if (!this.submitButton) return;

        const pending = this.hasPendingOrderAgreement();
        const shouldDisable = this.productUnavailable || pending;
        this.submitButton.toggleAttribute('disabled', shouldDisable);
        this.submitButton.setAttribute('aria-disabled', shouldDisable ? 'true' : 'false');

        if (!shouldDisable && this.submitButtonText && window.variantStrings?.addToCart) {
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      validateOrderAcknowledgements() {
        const firstUnacceptedCheckbox = this.getActiveOrderAcknowledgements().find(
          (checkbox) => !checkbox.checked
        );

        if (!firstUnacceptedCheckbox) return true;

        const warning = firstUnacceptedCheckbox.closest('[data-order-warning]');
        warning?.classList.add('order-warning--needs-acceptance');

        this.handleErrorMessage('Please tick the order acknowledgement box before adding this item to your cart.');
        warning?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        window.setTimeout(() => firstUnacceptedCheckbox.focus({ preventScroll: true }), 350);

        return false;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        this.productUnavailable = disable;
        this.submitButton.dataset.productUnavailable = disable ? 'true' : 'false';
        if (disable && text) this.submitButtonText.textContent = text;
        this.refreshOrderAgreement();
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise =
          typeof CartItems !== 'undefined'
            ? CartItems.fetchCartData()
            : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
